import { BehaviorSubject } from 'rxjs';
import {
  InviteClientContext,
  InviteServerContext,
  NameAddrHeader,
  PublishContext,
  Subscription,
  URI,
} from 'sip.js';
import { UserAgent } from 'sip.js/lib/api';
import { UA } from 'sip.js/lib/UA';
import { EPresenceStatusApi } from '@/shared/enums/presence.enum';
import { getExtensionFromUri } from '@/shared/helpers/get-from-uri.helper';
import { getUriFromAgent } from '@/shared/helpers/get-server-from-agent.helper';
import { randomHex } from '@/shared/helpers/random-hex.helper';
import { IAuthData } from '@/shared/interfaces/auth-data.interface';
import { logger } from './logger.service';
import { IAgentApi } from './solgari-api/interfaces/agent.interface';
import { LOCAL_STORAGE } from '@/shared/constants/localStorage';
import { SolgariApiService } from './solgari-api/solgari-api.service';
import { State } from './state.service';
import { Timer } from '@/shared/helpers/timer.helper';
import {
  ECallStatus,
  ESessionStatus,
} from '@/shared/enums/ringing-status.enum';
import { BusEvent, EBusEvent } from './bus-event/bus-event.service';
import { validatePhoneNumber } from '@/shared/helpers/common-make-call.helper';
import { IEntity } from '@/shared/interfaces/entity.interface';
import { Auth } from './auth.service';

export type DirectionType = 'outgoing' | 'incoming';

export type SessionState = 'active' | 'inactive' | 'idle';

export type RemoteUserType = 'unknown' | 'internal' | 'external';

export interface IStream {
  status: 'addstream' | 'removestream';
  stream: MediaProvider;
}

export interface IPresenceData {
  extension: string;
  status: EPresenceStatusApi;
  subscription: Subscription;
}

export interface IRemoteUser {
  name: string;
  phone: string;
  uriTarget: string;
  type: RemoteUserType;
  connectedUsers: IEntity[];
  queueName: string;
}

export interface IParticipant {
  name: string;
  phone: string;
  number?: string;
  uriTarget: string;
  externalUser?: unknown[];
}

export interface IConference {
  conferenceId: string;
  extensionNumber: string;
  subject?: string;
  customer: object;
  participants: IAgentApi[];
}

export interface ISession {
  id: string;
  primalSession: any;
  sessionState$: BehaviorSubject<ESessionStatus>;
  status: SessionState;
  timer: Timer;
  conferenceTimer?: Timer;
  xSessionId: string;
  /**
   * For specific customer: Case number associated with the session, if provided by SIP headers.
   */
  xCaseNumber?: string;
  /**
   * For specific customer: Serial number associated with the session, if provided by SIP headers.
   */
  xSerialNumber?: string;
  remoteUser: IRemoteUser;
  stream$: BehaviorSubject<IStream>;
  direction: DirectionType;

  participants: {
    myself: IParticipant;
    remote: IParticipant;
  };

  setRemoteUser: (type: RemoteUserType, entities: IEntity[]) => void;
  setExternal: (users: unknown[]) => void;
  getExternal: () => unknown[];
}

const RETRIEVE_PARTICIPANTS_INTERVAL = 5000;

export class SipJs {
  static userAgent: UA;
  static authData: IAuthData;
  static conference: IConference;
  static direction: DirectionType;
  static presences$: BehaviorSubject<IPresenceData[]> = new BehaviorSubject([]);
  static callStatus$: BehaviorSubject<ECallStatus> = new BehaviorSubject(null);
  static sessions$: BehaviorSubject<ISession[]> = new BehaviorSubject([]);
  static sessionId: string;
  static isStopped: boolean;
  static presenceExpireTime = 3600;
  static onUnload = false;
  static retrieveParticipantInterval: NodeJS.Timeout | null = null;
  static firstNotify = true;
  static isInitialRegistration = true;
  static lastTerminatedSession: ISession | null = null;

  static init(config: IAuthData) {
    const userAgentOptions: UA.Options = {
      autostart: false,
      log: {
        builtinEnabled: false,
        level: 2,
      },
      password: config.password,
      transportOptions: {
        wsServers: config.server,
        traceSip: false,
      },
      noAnswerTimeout: 30,
      uri: config.uri,
      register: true,
      sessionDescriptionHandlerFactoryOptions: {
        iceCheckingTimeout: 10000,
        alwaysAcquireMediaFirst: true,
        constraints: {
          audio: true,
          video: false,
        },
      },
    };

    this.authData = config;

    this.userAgent = new UA(userAgentOptions);

    // SIP registrations have a limited lifespan, usually defined by the expires parameter.
    // To keep the registration alive, the client must send a REGISTER request before the expiration time to refresh it.
    this.userAgent.on('registered', () => {
      const sessionIsActive = Auth.isLogged();
      if (
        !this.sessions.length &&
        sessionIsActive &&
        this.isInitialRegistration
      ) {
        BusEvent.sendEvent({ event: EBusEvent.SIP_LOGIN_SUCCESS });
        this.callStatus$.next(ECallStatus.PENDING);
        SipJs.subscribeToPresence(config.uri, true);
        this.isInitialRegistration = false;
      }
    });

    this.userAgent.on('registrationFailed', (err) => {
      logger.critical(`[SipJs] Registration failed `, err);
      BusEvent.sendEvent({ event: EBusEvent.SIP_LOGIN_FAILED });
    });

    this.userAgent.on('invite', (session) => {
      logger.log(`[SipJs] Invite started `, session);

      this.newRTCSessionHandler(session, 'incoming');
    });

    this.userAgent.on('message', (message: any) => {
      logger.log(`[SipJs] New message `, message);
      if (SipJs.isStopped) return;

      const res = JSON.parse(message.request.body);
      const session = this.sessions[0];

      if (res.event === 'assignClient__attended_transfer') {
        const { user, userType } = res.message;
        session.setRemoteUser(userType, [user]);
        State.setState({ activeEntities: [res.message] });
        this.setSession(session);
      } else if (res.event === 'inviteAgent__send_conference_data') {
        this.conference = { ...res.data.conference };
        if (this.retrieveParticipantInterval) {
          clearInterval(this.retrieveParticipantInterval);
          this.retrieveParticipantInterval = null;
        }
        if (this.conference) {
          this.getActualParticipants(this.conference.conferenceId);
          this.retrieveParticipantInterval = setInterval(() => {
            this.getActualParticipants(this.conference.conferenceId);
          }, RETRIEVE_PARTICIPANTS_INTERVAL);
        }
      }
    });

    this.userAgent.start();
  }

  private static setSessionEvents(niceSession: ISession) {
    const {
      primalSession: session,
      sessionState$,
      timer,
      stream$,
    } = niceSession;

    session.on('accepted', (response: any) => {
      logger.log(`[SipJs] Session accepted `, response);

      this.setAllInactive();

      niceSession.status = 'active';
      niceSession.xSessionId ??= getSipHeaderValue(
        response?.headers,
        'X-Session-Id',
      );
      timer.startTimer(1);

      this.addConnectionListener(niceSession);

      sessionState$.next(ESessionStatus.ACCEPTED);
      SipJs.pushNotificationWithoutQueue(EPresenceStatusApi.ON_A_CALL);

      const activeSession = this.getSessionByType('active');

      this.setSession(activeSession);

      this.callStatus$.next(ECallStatus.CONNECTED);

      if (this.conference) {
        if (niceSession?.conferenceTimer) {
          niceSession.conferenceTimer.startTimer(1);
        }
        if (this.retrieveParticipantInterval) {
          clearInterval(this.retrieveParticipantInterval);
          this.retrieveParticipantInterval = null;
        }
        this.getActualParticipants(this.conference.conferenceId);
        this.retrieveParticipantInterval = setInterval(() => {
          this.getActualParticipants(this.conference?.conferenceId);
        }, RETRIEVE_PARTICIPANTS_INTERVAL);
      }
    });

    session.on('failed', (response: { method: string }, cause: string) => {
      logger.log(`[SipJs] Session failed `, { response, cause });
      this.clearConference();
      if (
        ['Canceled', 'No Answer'].includes(cause) ||
        response?.method == 'CANCEL'
      ) {
        State.setState({ wrapUpQueue: null });
      }
      if (response?.method == 'CANCEL') {
        State.setState({ wrapUpQueue: null });
      }
      localStorage.removeItem(LOCAL_STORAGE.LS_SESSION_IS_ACTIVE);
      sessionState$.next(ESessionStatus.FAILED);
      timer.stopTimer();
      this.removeSession(niceSession);
      this.setCallStatusBySessionLength();
      setTimeout(() => {
        BusEvent.sendEvent({ event: EBusEvent.UPD_HISTORY });
      }, 500);
    });

    session.on('terminated', (message: string, cause: string) => {
      logger.log(`[SipJs] Session terminated `, message, cause);

      const storedReason = sessionState$.getValue();
      const storedPresence = localStorage.getItem(
        LOCAL_STORAGE.LS_MANUAL_PRESENCE,
      ) as EPresenceStatusApi;

      timer.stopTimer();
      if (niceSession.conferenceTimer) {
        niceSession.conferenceTimer.stopTimer();
      }
      this.lastTerminatedSession = niceSession;
      this.removeSession(niceSession);

      if (
        storedReason !== ESessionStatus.ENDED_BY_BLIND_TRANSFER &&
        this.conference
      ) {
        SipJs.clearConference();
      }

      localStorage.removeItem(LOCAL_STORAGE.LS_SESSION_IS_ACTIVE);

      this.setCallStatusBySessionLength();

      sessionState$.next(ESessionStatus.PENDING);

      const { wrapUpQueue } = State.state$.getValue();

      if (
        storedPresence != EPresenceStatusApi.WRAP_UP &&
        !wrapUpQueue?.isWorking
      ) {
        SipJs.pushNotification(
          storedPresence || EPresenceStatusApi.AVAILABLE,
          true,
        );
      }
      setTimeout(() => {
        BusEvent.sendEvent({ event: EBusEvent.UPD_HISTORY });
      }, 500);
    });

    // Track hold call event
    session.on('directionChanged', function () {
      const direction = session.sessionDescriptionHandler.getDirection();
      if (direction === 'sendonly') {
        stream$.next({ status: 'removestream', stream: null });
      }
    });
  }

  static get sessions(): ISession[] {
    return this.sessions$.getValue();
  }

  private static addConnectionListener(niceSession: ISession) {
    const { primalSession, stream$ } = niceSession;
    const peerConnection =
      primalSession.sessionDescriptionHandler.peerConnection;
    const initialStream = peerConnection.getRemoteStreams()[0];

    stream$.next({ status: 'addstream', stream: initialStream });

    peerConnection.addEventListener('track', (track: RTCTrackEvent) => {
      stream$.next({ status: 'addstream', stream: track.streams[0] });
    });
  }

  private static removeSession(session: ISession): void {
    const sessions = this.sessions.filter((s) => s.id !== session.id);

    this.sessions$.next(sessions);
  }

  private static setCallStatusBySessionLength(): void {
    if (this.sessions.length === 1) {
      this.callStatus$.next(ECallStatus.DISCONNECTED);
    } else {
      this.callStatus$.next(ECallStatus.PENDING);
    }
  }

  static getActualParticipants = async (conferenceId: string) => {
    if (!this.conference || !conferenceId) {
      return;
    }

    const participantsData =
      await SolgariApiService.getConferenceParticipants(conferenceId);
    const { participants } = participantsData.data;

    this.conference.participants = participants
      .map(
        ({
          name,
          email,
          extensionNumber,
        }: {
          name: string;
          email: string;
          extensionNumber: string;
        }) => {
          return {
            fullName: name,
            email,
            extensionId: extensionNumber,
          };
        },
      )
      .filter((participant: IAgentApi) => !!participant.extensionId);
    if (participants.length > 1) {
      BusEvent.sendEvent({ event: EBusEvent.UPD_ACTUAL_PARTICIPANTS });
    } else {
      this.declineAllCalls();
    }
  };

  private static setAllInactive(): void {
    if (this.sessions.length === 1) {
      return;
    }

    this.sessions.forEach((session) => {
      if (session.status === 'idle') {
        return;
      }

      if (SipJs.isOnHold(session.id) && session.status === 'inactive') {
        return;
      }

      SipJs.holdCall(session.id);
    });
  }

  private static clearConference(): void {
    const activeSession = this.sessions[0];
    if (activeSession?.conferenceTimer) {
      activeSession.conferenceTimer.stopTimer();
    }

    this.conference &&
      this.checkToDeleteConference(this.conference.conferenceId);

    this.conference = null;
    this.retrieveParticipantInterval &&
      clearInterval(this.retrieveParticipantInterval);
    this.retrieveParticipantInterval = null;
  }

  static checkToDeleteConference = async (conferenceId: string) => {
    let count = 0;

    if (this.onUnload) {
      // it is not possible to await async requests on unload event, so get the latest participants data instead
      count = this.conference.participants.length;
    } else {
      const participantsData =
        await SolgariApiService.getConferenceParticipants(conferenceId);
      count = participantsData.data.count;
    }

    if (count < 2) {
      SolgariApiService.deleteConference(conferenceId);
    }
  };

  static sendMessage(uri: string, message: string) {
    this.userAgent.message(uri, message);
  }

  static holdCall(sessionId: string): void {
    const { primalSession } = this.getSessionById(sessionId);
    primalSession.hold();
  }

  static unHoldCall(sessionId: string): void {
    const { primalSession } = this.getSessionById(sessionId);
    this.setAllInactive();
    primalSession.unhold();
  }

  static isOnHold(sessionId: string): boolean {
    const { primalSession } = this.getSessionById(sessionId);

    return primalSession.localHold;
  }

  static unMuteCall(sessionId: string) {
    const { primalSession } = this.getSessionById(sessionId);
    const peerConnection =
      primalSession.sessionDescriptionHandler.peerConnection;
    peerConnection.getLocalStreams().forEach((stream: MediaStream) => {
      stream.getAudioTracks().forEach((track: MediaStreamTrack) => {
        track.enabled = true;
      });
    });
  }

  static muteCall(sessionId: string) {
    const { primalSession } = this.getSessionById(sessionId);
    const peerConnection =
      primalSession.sessionDescriptionHandler.peerConnection;
    peerConnection.getLocalStreams().forEach((stream: MediaStream) => {
      stream.getAudioTracks().forEach((track: MediaStreamTrack) => {
        track.enabled = false;
      });
    });
  }

  static makeCall(phone: string, allowSecondCall?: boolean) {
    logger.log(`[SipJs] Start call to `, phone);
    if (this.sessions.length && !allowSecondCall) return;

    if (this.sessions.length === 2) return;

    const inviter = this.userAgent.invite(phone, {
      sessionDescriptionHandlerOptions: {
        constraints: {
          audio: true,
          video: false,
        },
      },
    });

    this.newRTCSessionHandler(inviter, 'outgoing');
  }

  static getSessionByType(type: SessionState): ISession {
    const session = this.sessions.find((s) => s.status === type);

    return session;
  }

  static getAgentsOnCall(): IAgentApi[] {
    return this.conference?.participants || [];
  }

  static getSessionById(id: string): ISession {
    return this.sessions.find((s) => s.id === id);
  }

  static stopSip(): void {
    logger.log('[SipJs] Stop sip');
    this.userAgent?.stop();
    this.isStopped = true;
  }

  static isMultiSession(): boolean {
    return this.sessions.length > 1;
  }

  static getInvitedForTransferSession(): ISession {
    if (this.isMultiSession()) {
      return this.sessions[this.sessions.length - 1];
    }
    return;
  }

  static sendTone(tone: string): void {
    let targetSession: ISession;
    if (this.isMultiSession()) {
      targetSession = this.sessions[this.sessions.length - 1];
    } else {
      targetSession = this.sessions.find((s) =>
        ['active', 'inactive'].includes(s.status),
      );
    }

    const primalSession = targetSession?.primalSession;
    if (!primalSession) return;
    primalSession.dtmf(tone);
  }

  static end(): void {
    this.userAgent?.stop();
    this.userAgent = null;
  }

  static declineAllCalls(): void {
    this.sessions.forEach((session: ISession) => {
      this.declineCall(session.id);
    });
    SipJs.pushNotification(EPresenceStatusApi.AVAILABLE, true);
  }

  static declineCall(
    sessionId: string,
    options?: { status_code: number; reason_phrase: string },
  ): void {
    logger.log(`[SipJs] Decline call with sessionId: ${sessionId}`);
    const { primalSession } = this.getSessionById(sessionId);

    if (!primalSession) return;

    // Do not erase wrapUpQueue if call was declined with status code 486 (In Wrap-up)
    if (!primalSession.hasAnswer && options?.status_code !== 486) {
      State.setState({ wrapUpQueue: null });
    }
    primalSession.terminate(options);
  }

  static makeSubscriptions(agents: IAgentApi[]) {
    agents.forEach((agent) => {
      const entity = SipJs.presences$
        .getValue()
        .find((s) => s.extension === String(agent.extensionId));

      if (entity) {
        return;
      }

      const uri = getUriFromAgent(agent);
      this.subscribeToPresence(uri);
    });
  }

  static acceptCall(sessionId: string): void {
    logger.log(`[SipJs] Accept call with sessionId: ${sessionId}`);
    const { primalSession } = this.getSessionById(sessionId);

    if (!primalSession) return;

    primalSession.accept();
  }

  static blindTransfer(
    sessionId: string,
    target: IAgentApi | string,
    isConference?: boolean,
  ): void {
    const { primalSession } = this.getSessionById(sessionId);

    if (!primalSession) return;

    let uri = '';

    if (isConference) {
      uri = String(target);
    } else if (typeof target === 'string') {
      uri = validatePhoneNumber(target);
    } else {
      uri = getUriFromAgent(target);
    }

    const options = {
      receiveResponse: function (
        response: {
          reasonPhrase: string;
        } & Record<string, unknown>,
      ) {
        if (response.reasonPhrase === 'Accepted') {
          const session = SipJs.getSessionById(sessionId);
          if (session?.sessionState$) {
            session.sessionState$.next(ESessionStatus.ENDED_BY_BLIND_TRANSFER);
          }
          primalSession.terminate();
          if (isConference) {
            SipJs.makeCall(uri);
          }
        }
      },
    };
    logger.log(`[SipJs] Start blind transfer with sessionId: ${sessionId}`);
    primalSession.refer(uri, options);
  }

  static attendedTransfer(
    clientSessionId: string,
    agentSessionId: string,
  ): void {
    const clientSession = this.getSessionById(clientSessionId);
    const agentSession = this.getSessionById(agentSessionId);

    if (!clientSession || !agentSession) {
      logger.log('[SipJs]: There is no session');
      return;
    }

    const uri = this.getUriTarget(
      agentSession.primalSession.remoteIdentity.uri,
    );

    const options = {
      replaces: agentSession.primalSession,
      receiveResponse: (
        response: { reasonPhrase: string } & Record<string, unknown>,
      ) => {
        if (response.reasonPhrase === 'Accepted') {
          const user = clientSession.remoteUser.connectedUsers[0];
          const body = {
            event: 'assignClient__attended_transfer',
            message: { user, userType: clientSession.remoteUser.type },
          };
          const strBody = JSON.stringify(body);

          clientSession.primalSession.terminate();

          this.sendMessage(uri, strBody);
        }
      },
    };

    logger.log(
      `[SipJs] Start attended transfer - client: ${clientSessionId}, agent: ${agentSessionId}`,
    );
    clientSession.primalSession.refer(agentSession.primalSession, options);
  }

  static unSubscribeAll(): void {
    const presences = SipJs.presences$.getValue();
    presences.forEach((p) => {
      p.subscription.unsubscribe();
    });
  }

  static makeUnSubscriptions(agents: IAgentApi[]) {
    if (!agents?.length) {
      return;
    }

    agents.forEach((agent) => {
      const prevState = SipJs.presences$.getValue();
      const entity = prevState.find(
        (s) => s.extension === String(agent.extensionId),
      );

      if (!entity) {
        return;
      }

      entity.subscription.unsubscribe();
      const newState = prevState.filter(
        (s) => s.extension !== String(agent.extensionId),
      );

      SipJs.presences$.next(newState);
    });
  }

  static pauseOrResumeCall(
    extension: string,
    sessionId: string,
    isPause: boolean,
  ): void {
    const options = {
      contentType: 'application/x-media-control+json',
    };
    let pauseOperation: string = 'pause-audio-recording';
    if (!isPause) {
      pauseOperation = 'resume-audio-recording';
    }
    this.userAgent.message(
      extension,
      JSON.stringify({
        session_id: sessionId,
        operation: pauseOperation,
      }),
      options,
    );
  }

  static getUriTarget(uri: URI): string {
    return `${uri.scheme}:${uri.user}@${uri.host}`;
  }

  private static getRemotePhone(remoteUser: NameAddrHeader): string {
    const inboundPhoneNumber = remoteUser.uri.user;
    const isInboundPhoneNumberLocal =
      inboundPhoneNumber.indexOf('0') === 0 &&
      inboundPhoneNumber.indexOf('00') !== 0;
    const phoneNumber = isInboundPhoneNumberLocal
      ? `44${inboundPhoneNumber.substring(1)}`
      : inboundPhoneNumber;
    return phoneNumber;
  }

  private static createParticipants(
    session: InviteServerContext | InviteClientContext,
  ): {
    myself: IParticipant;
    remote: IParticipant;
  } {
    const myself: IParticipant = {
      name: session.localIdentity.displayName,
      phone: session.localIdentity.uri.user,
      uriTarget: this.getUriTarget(session.localIdentity.uri),
    };

    const remote: IParticipant = {
      name: session.remoteIdentity.displayName,
      phone: this.getRemotePhone(session.remoteIdentity),
      uriTarget: this.getUriTarget(session.remoteIdentity.uri),
    };
    return { myself, remote };
  }

  private static createRemoteUser(
    session: InviteServerContext | InviteClientContext,
  ): IRemoteUser {
    // TODO investigate and get rid of this crap below
    const parties =
      (session as any)?.request?.headers?.['Remote-Party-Id']?.reverse() ?? [];
    const remotePartyId = parties[0]?.raw ?? '';
    const queueName = remotePartyId.match(/^".+"/g)?.[0];

    return {
      name: session.remoteIdentity.displayName,
      phone: this.getRemotePhone(session.remoteIdentity),
      uriTarget: this.getUriTarget(session.remoteIdentity.uri),
      type: 'unknown',
      connectedUsers: [],
      queueName,
    };
  }

  private static setSession(session: ISession): void {
    const sessions = this.sessions.filter((s) => s.id !== session.id);

    this.sessions$.next([...sessions, session]);
  }

  private static newRTCSessionHandler(
    invite: InviteServerContext | InviteClientContext,
    type: DirectionType,
  ): void {
    localStorage.setItem(LOCAL_STORAGE.LS_SESSION_IS_ACTIVE, 'true');
    const sessionState$ = new BehaviorSubject<ESessionStatus>(
      ESessionStatus.INIT,
    );
    const stream$ = new BehaviorSubject<IStream>({
      status: null,
      stream: null,
    });
    const timer = new Timer();
    const xIdObj = invite?.request?.headers['X-Session-Id'] as any;
    let xSessionIdValue;
    if (xIdObj) {
      const xId = xIdObj[0] as any;
      xSessionIdValue = xId?.raw;
    }

    const niceSession: ISession = {
      id: invite.id,
      primalSession: invite,
      sessionState$,
      status: 'idle',
      timer,
      conferenceTimer: new Timer(),
      xSessionId: xSessionIdValue,
      xCaseNumber: getSipHeaderValue(invite?.request?.headers, 'X-Casenumber'),
      xSerialNumber: getSipHeaderValue(
        invite?.request?.headers,
        'X-Serialnumber',
      ),
      participants: SipJs.createParticipants(invite),
      remoteUser: SipJs.createRemoteUser(invite),
      stream$,
      direction: type,

      setRemoteUser: function (type: RemoteUserType, entities: IEntity[]) {
        this.remoteUser.type = type;
        this.remoteUser.connectedUsers = entities;
      },
      getExternal: function () {
        return this.participants.remote.externalUser;
      },
      setExternal: function (users: unknown[]) {
        this.participants.remote.externalUser = users;
      },
    };

    this.setSession(niceSession);
    this.setSessionEvents(niceSession);

    if (type === 'incoming') {
      const presence = SipJs.getMySelfPresence();

      if (
        (presence?.status &&
          ![EPresenceStatusApi.AVAILABLE, EPresenceStatusApi.WRAP_UP].includes(
            presence.status,
          )) ||
        SipJs.isStopped
      ) {
        return;
      }
      this.callStatus$.next(ECallStatus.RINGING_IN);
    } else {
      this.callStatus$.next(ECallStatus.RINGING_OUT);

      // Skip ringing out screen phase for conference, we still need it to blink, as it will add previous session participant object to UI (don't ask why)
      if (this.conference) {
        this.callStatus$.next(ECallStatus.CONNECTED);
      }
    }
  }

  static subscribeToPresence(uri: string, self = false): void {
    const targetURI = UserAgent.makeURI(uri);
    const subscriber = this.userAgent.subscribe(targetURI, 'presence', {});

    subscriber.on('notify', (notification) => {
      let presence: EPresenceStatusApi = EPresenceStatusApi.AVAILABLE;
      let presenceHistory: EPresenceStatusApi[];
      let extension = notification.request.from.uri.user;

      // If there is no body this is just a refresh
      if (!notification.request.body || this.isStopped) {
        return;
      }

      if (notification.request.headers.Event[0].raw === 'presence') {
        // Get the extension directly from the notification
        extension = notification.request.from.uri.user;

        // Parse the body xml text for the 'note' node and get the presence value
        const parser = new DOMParser();

        // Parse the body into an xml document
        const xmldoc = parser.parseFromString(
          notification.request.body,
          'text/xml',
        );

        // Try for a note element and if we don't have one settle for a basic element
        try {
          // Get a list of tuples
          const tupleList = xmldoc.getElementsByTagName('tuple');

          const sortedTuples = [...tupleList].sort((a, b) => {
            const t1 = a.getElementsByTagName('timestamp');
            const t2 = b.getElementsByTagName('timestamp');

            const time1 = new Date(t1[0].childNodes[0].nodeValue).getTime();
            const time2 = new Date(t2[0].childNodes[0].nodeValue).getTime();

            return time2 - time1;
          });

          presenceHistory = sortedTuples.map((tuple: Element) => {
            const note = tuple.getElementsByTagName('note');
            if (!self && !note.length) {
              const status =
                tuple.getElementsByTagName('basic')[0]?.textContent;
              return status === 'open'
                ? EPresenceStatusApi.AVAILABLE
                : EPresenceStatusApi.OFFLINE;
            }
            return note[0]?.childNodes[0].nodeValue as EPresenceStatusApi;
          });

          presence = presenceHistory[0] || EPresenceStatusApi.AVAILABLE;

          if (this.firstNotify) {
            const statusToSend =
              presence === EPresenceStatusApi.ON_A_CALL ||
              presence === EPresenceStatusApi.WRAP_UP
                ? EPresenceStatusApi.AVAILABLE
                : presence;
            presence = statusToSend;

            SipJs.setCodeForServer(statusToSend);
            SolgariApiService.setSolgariPresence(statusToSend);
            this.firstNotify = false;
          }
        } catch (err) {
          logger.error(`[SipJs] Caught presence issue: `, err);
        }
      }

      const prevState = SipJs.presences$.getValue();
      const filteredState = prevState.filter((p) => p.extension !== extension);

      SipJs.presences$.next([
        ...filteredState,
        {
          extension,
          status: presence,
          subscription: subscriber,
        },
      ]);
    });
  }

  static pushNotificationWithoutQueue(note: EPresenceStatusApi): void {
    this.pushNotification(note, false);
  }

  static async pushNotification(
    note: EPresenceStatusApi,
    sendQueueUpdate: boolean,
  ): Promise<void> {
    logger.log(
      `[SipJs] Push notification: ${note}, sendQueueUpdate: ${sendQueueUpdate}`,
    );
    if (this.isStopped) return;
    let eTag: string = localStorage.getItem('eTag');
    let publishSession: PublishContext = null;

    const targetURI = UserAgent.makeURI(this.authData.uri);
    const content = `<?xml version="1.0" encoding="UTF-8"?>
        <presence xmlns="urn:ietf:params:xml:ns:pidf"
          entity="${this.authData.uri}">
            <tuple id="${randomHex(16)}">
                <status>
                    <basic>open</basic>
                </status>
                <note>${note}</note>
                <timestamp>${new Date().toISOString()}</timestamp>
            </tuple>
        </presence>`;

    const options =
      !eTag ||
      parseInt(localStorage.getItem('eTagTimeout')) < Date.now().valueOf()
        ? {
            contentType: 'application/pidf+xml',
          }
        : {
            extraHeaders: ['SIP-If-Match: ' + eTag],
            contentType: 'application/pidf+xml',
            expires: this.presenceExpireTime,
          };

    publishSession = this.userAgent.publish(
      targetURI,
      'presence',
      content,
      options,
    );

    publishSession.on('published', (response) => {
      if (sendQueueUpdate) {
        SipJs.setCodeForServer(note);
      }
      eTag = response.getHeader('SIP-ETag');
      const newDate = new Date();
      newDate.setHours(newDate.getHours() + 1);
      localStorage.setItem('eTagTimeout', newDate.valueOf().toString());
      localStorage.setItem('eTag', eTag);
    });
    await SolgariApiService.setSolgariPresence(note);
  }

  static getMySelfPresence(): IPresenceData {
    const presences = SipJs.presences$.getValue();

    return presences.find(
      (p) => p.extension === getExtensionFromUri(this.authData.uri),
    );
  }

  static setCodeForServer(presence: EPresenceStatusApi): void {
    const codeToSend =
      presence === EPresenceStatusApi.AVAILABLE ? '*51*' : '*52*';

    if (presence === EPresenceStatusApi.WRAP_UP) {
      return;
    }
    const uriQueue = UserAgent.makeURI(
      `sip:${codeToSend}@${this.authData.tenantDomain}`,
    );

    const optionsQueue = {
      sessionDescriptionHandlerOptions: {
        constraints: {
          audio: true,
          video: false,
        },
      },
    };
    logger.log(`[SipJs] Set code for server: ${codeToSend}`);
    this.userAgent.invite(uriQueue, optionsQueue);
  }
}

function getSipHeaderValue(
  headers: any,
  headerName: string,
): string | undefined {
  const header = headers?.[headerName];
  if (
    header &&
    Array.isArray(header) &&
    header[0] &&
    typeof header[0].raw === 'string'
  ) {
    return header[0].raw;
  }
  return undefined;
}
